import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { solutionColors } from '../../styles/solutions';
import { Container, Section } from '../../styles/GlobalStyles';

const SolutionsContainer = styled(Section)`
  background: ${theme.colors.neutral.light};
  padding: ${theme.spacing['3xl']} 0;

  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing.xl} 0;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.lg} 0;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing['2xl']};

  @media (max-width: ${theme.breakpoints.md}) {
    margin-bottom: ${theme.spacing.xl};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    margin-bottom: ${theme.spacing.lg};
  }
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  margin-bottom: ${theme.spacing.sm};
  
  .highlight {
    color: ${theme.colors.primary.synapse};
    position: relative;
  }
  
  .highlight::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    right: 0;
    height: 3px;
    background: ${theme.colors.primary.accent};
    border-radius: 2px;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize['3xl']};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.fontSize['2xl']};
    margin-bottom: ${theme.spacing.xs};
  }
`;

const SectionSubtitle = styled.p`
  font-size: ${theme.typography.fontSize.xl};
  color: ${theme.colors.neutral.gray600};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin: 0;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize.lg};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.fontSize.base};
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing['2xl']};
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.xl};
  }

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.lg};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }
`;

const ProductCard = styled.div<{ color: string }>`
  background: ${theme.colors.neutral.white};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.xl};
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: ${theme.spacing.md};
  transition: all ${theme.transitions.normal};
  /* Mesma borda e elevação dos cards das páginas de solução (FeatureCard). */
  border: 2px solid ${theme.colors.neutral.gray200};
  cursor: pointer;
  min-height: 200px;

  &:hover {
    transform: translateY(-4px);
    border-color: ${props => props.color};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.lg};
    min-height: 180px;
  }
`;

const ProductHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.sm}) {
    gap: ${theme.spacing.sm};
  }
`;

const ProductIconContainer = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.lg};
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.neutral.white};
  flex-shrink: 0;
  transition: all ${theme.transitions.normal};

  ${ProductCard}:hover & {
    transform: scale(1.05);
  }

  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    width: 44px;
    height: 44px;

    svg {
      width: 22px;
      height: 22px;
    }
  }
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  flex: 1;
`;

const ProductTitle = styled.h3`
  color: ${theme.colors.primary.data};
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  margin: 0;
  line-height: ${theme.typography.lineHeight.tight};

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.fontSize.base};
  }
`;

const ProductDescription = styled.p`
  color: ${theme.colors.neutral.gray600};
  line-height: ${theme.typography.lineHeight.relaxed};
  font-size: ${theme.typography.fontSize.sm};
  margin: 0;

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.fontSize.xs};
  }
`;

/* Botão alinhado à esquerda, no mesmo eixo do título e da descrição. */
const ProductFooter = styled.div`
  display: flex;
  align-items: center;
`;

const ProductButton = styled.button<{ color: string }>`
  background: ${props => props.color};
  color: ${theme.colors.neutral.white};
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.xs} ${theme.spacing.lg};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  white-space: nowrap;

  &:hover {
    transform: translateX(4px);
    box-shadow: 0 4px 12px ${props => props.color}40;
  }

  &:active {
    transform: translateX(2px);
  }

  svg {
    transition: transform ${theme.transitions.fast};
  }

  &:hover svg {
    transform: translateX(2px);
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.xs} ${theme.spacing.md};
    font-size: ${theme.typography.fontSize.xs};
  }
`;

/**
 * Ícones dos cards de solução.
 *
 * O contêiner aplica `fill: currentColor` (branco), então cada ícone precisa ser
 * uma silhueta de cor única — vazados são feitos com `fillRule="evenodd"`, nunca
 * com formas brancas sobrepostas, que sumiriam sobre o fundo colorido.
 */
const AgentIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="2.6" r="1.4" />
    <rect x="11.2" y="3.4" width="1.6" height="2.2" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8 5.4h8a4 4 0 0 1 4 4v6.2a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V9.4a4 4 0 0 1 4-4Zm1.3 5a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2Zm5.4 0a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2Z"
    />
  </svg>
);

const AIModelIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2.2l1.75 4.55L18.3 8.5l-4.55 1.75L12 14.8l-1.75-4.55L5.7 8.5l4.55-1.75L12 2.2Z" />
    <path d="M18.1 13.6l.85 2.2 2.2.85-2.2.85-.85 2.2-.85-2.2-2.2-.85 2.2-.85.85-2.2Z" />
    <path d="M6.3 14.8l.65 1.7 1.7.65-1.7.65-.65 1.7-.65-1.7-1.7-.65 1.7-.65.65-1.7Z" />
  </svg>
);

const ConsultingIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <rect x="3.4" y="12" width="4" height="7.6" rx="1.4" />
    <rect x="10" y="8.4" width="4" height="11.2" rx="1.4" />
    <rect x="16.6" y="4.4" width="4" height="15.2" rx="1.4" />
  </svg>
);

const Solutions: React.FC = () => {
  const navigate = useNavigate();
  
  const products = [
    {
      id: 'agentes',
      title: 'Agentes Inteligentes',
      icon: <AgentIcon />,
      color: solutionColors.agentes,
      description: 'Automação que aprende e se adapta. Vai além do RPA tradicional com decisões inteligentes em tempo real.',
      path: '/agentes-inteligentes',
    },
    {
      id: 'modelos-ia',
      title: 'Modelos de IA',
      icon: <AIModelIcon />,
      color: solutionColors['modelos-ia'],
      description: 'IA personalizada para seu negócio. Do conceito ao deploy, criamos modelos que entregam resultados reais.',
      path: '/modelos-ia',
    },
    {
      id: 'consultoria',
      title: 'Consultoria & Dados',
      icon: <ConsultingIcon />,
      color: solutionColors.consultoria,
      description: 'Estratégia completa em IA e dados. Arquiteturas robustas com governança e conformidade LGPD.',
      path: '/consultoria-dados',
    }
  ];

  const handleProductClick = (path: string) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <SolutionsContainer id="solutions">
      <Container>
        <SectionHeader>
          <SectionTitle>
            Nossas <span className="highlight">Soluções</span>
          </SectionTitle>
          
          <SectionSubtitle>
            Conectamos dados a decisões estratégicas com IA, automação inteligente e governança
          </SectionSubtitle>
        </SectionHeader>

        <ProductsGrid>
          {products.map((product) => (
            <ProductCard key={product.id} color={product.color} onClick={() => handleProductClick(product.path)}>
              <ProductHeader>
                <ProductIconContainer color={product.color}>
                  {product.icon}
                </ProductIconContainer>
                
                <ProductInfo>
                  <ProductTitle>{product.title}</ProductTitle>
                </ProductInfo>
              </ProductHeader>
              
              <ProductDescription>
                {product.description}
              </ProductDescription>
              
              <ProductFooter>
                <ProductButton color={product.color} onClick={(e) => { e.stopPropagation(); handleProductClick(product.path); }}>
                  Conhecer
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </ProductButton>
              </ProductFooter>
            </ProductCard>
          ))}
        </ProductsGrid>
      </Container>
    </SolutionsContainer>
  );
};

export default Solutions; 